// Database service using IndexedDB for client-side storage
// This provides a SQL-like interface for managing blog posts

class DatabaseService {
  constructor() {
    this.dbName = 'BlogDatabase';
    this.dbVersion = 2; // Increment version to add category index
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        reject(new Error('Database initialization timeout'));
      }, 3000);

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        clearTimeout(timeout);
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        clearTimeout(timeout);
        this.db = request.result;
        console.log('Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        console.log('Database upgrade needed, version:', event.oldVersion, '->', event.newVersion);
        const db = event.target.result;

        try {
          // Create posts table
          if (!db.objectStoreNames.contains('posts')) {
            const postsStore = db.createObjectStore('posts', { keyPath: 'id', autoIncrement: true });
            postsStore.createIndex('title', 'title', { unique: false });
            postsStore.createIndex('date', 'date', { unique: false });
            postsStore.createIndex('route', 'route', { unique: true });
            postsStore.createIndex('requiresAuth', 'requiresAuth', { unique: false });
            postsStore.createIndex('isHidden', 'isHidden', { unique: false });
            console.log('Posts store created');
          }

          // Create archives table
          if (!db.objectStoreNames.contains('archives')) {
            const archivesStore = db.createObjectStore('archives', { keyPath: 'id', autoIncrement: true });
            archivesStore.createIndex('year', 'year', { unique: false });
            archivesStore.createIndex('month', 'month', { unique: false });
            archivesStore.createIndex('date', 'date', { unique: false });
            archivesStore.createIndex('route', 'route', { unique: true });
            archivesStore.createIndex('category', 'category', { unique: false });
            console.log('Archives store created');
          } else {
            // Handle upgrade: add category index if it doesn't exist
            const transaction = event.target.transaction;
            const archivesStore = transaction.objectStore('archives');
            if (!archivesStore.indexNames.contains('category')) {
              archivesStore.createIndex('category', 'category', { unique: false });
              console.log('Category index added to archives store');
            }
          }
        } catch (error) {
          clearTimeout(timeout);
          console.error('Error during database upgrade:', error);
          reject(error);
        }
      };

      request.onblocked = () => {
        console.warn('Database upgrade blocked by another tab');
        // Don't reject, just warn
      };
    });
  }

  // Posts CRUD operations
  async createPost(postData) {
    const transaction = this.db.transaction(['posts'], 'readwrite');
    const store = transaction.objectStore('posts');
    return new Promise((resolve, reject) => {
      const request = store.add({
        title: postData.title,
        description: postData.description,
        content: postData.content,
        route: postData.route,
        date: postData.date || new Date().toISOString(),
        requiresAuth: postData.requiresAuth || false,
        isHidden: postData.isHidden || false,
        sections: postData.sections || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPost(id) {
    const transaction = this.db.transaction(['posts'], 'readonly');
    const store = transaction.objectStore('posts');
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPostByRoute(route) {
    const transaction = this.db.transaction(['posts'], 'readonly');
    const store = transaction.objectStore('posts');
    const index = store.index('route');
    return new Promise((resolve, reject) => {
      const request = index.get(route);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllPosts() {
    const transaction = this.db.transaction(['posts'], 'readonly');
    const store = transaction.objectStore('posts');
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPublicPosts() {
    const transaction = this.db.transaction(['posts'], 'readonly');
    const store = transaction.objectStore('posts');
    const index = store.index('requiresAuth');
    return new Promise((resolve, reject) => {
      const request = index.getAll(false);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updatePost(id, postData) {
    const transaction = this.db.transaction(['posts'], 'readwrite');
    const store = transaction.objectStore('posts');
    return new Promise((resolve, reject) => {
      const request = store.put({
        id: id,
        ...postData,
        updatedAt: new Date().toISOString()
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePost(id) {
    const transaction = this.db.transaction(['posts'], 'readwrite');
    const store = transaction.objectStore('posts');
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateArchivePost(id, archiveData) {
    const transaction = this.db.transaction(['archives'], 'readwrite');
    const store = transaction.objectStore('archives');
    return new Promise((resolve, reject) => {
      const request = store.put({
        id: id,
        ...archiveData,
        updatedAt: new Date().toISOString()
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteArchivePost(id) {
    const transaction = this.db.transaction(['archives'], 'readwrite');
    const store = transaction.objectStore('archives');
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Archives CRUD operations
  async createArchivePost(archiveData) {
    const transaction = this.db.transaction(['archives'], 'readwrite');
    const store = transaction.objectStore('archives');
    return new Promise((resolve, reject) => {
      const request = store.add({
        title: archiveData.title,
        content: archiveData.content,
        excerpt: archiveData.excerpt,
        route: archiveData.route,
        year: archiveData.year,
        month: archiveData.month,
        date: archiveData.date || new Date().toISOString(),
        category: archiveData.category || 'archive', // Default to 'archive' category
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getArchivesByYear(year) {
    const transaction = this.db.transaction(['archives'], 'readonly');
    const store = transaction.objectStore('archives');
    const index = store.index('year');
    return new Promise((resolve, reject) => {
      const request = index.getAll(year);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllArchives() {
    const transaction = this.db.transaction(['archives'], 'readonly');
    const store = transaction.objectStore('archives');
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getArchiveByRoute(route) {
    const transaction = this.db.transaction(['archives'], 'readonly');
    const store = transaction.objectStore('archives');
    const index = store.index('route');
    return new Promise((resolve, reject) => {
      const request = index.get(route);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Category-based archive operations
  async getArchivesByCategory(category) {
    const transaction = this.db.transaction(['archives'], 'readonly');
    const store = transaction.objectStore('archives');
    const index = store.index('category');
    return new Promise((resolve, reject) => {
      const request = index.getAll(category);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getArchivesByCategoryAndYear(category, year) {
    const transaction = this.db.transaction(['archives'], 'readonly');
    const store = transaction.objectStore('archives');
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const allArchives = request.result;
        const filtered = allArchives.filter(archive =>
          archive.category === category && archive.year === year
        );
        resolve(filtered);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Journal CRUD operations (temporarily disabled)
  async createJournalEntry(entryData) {
    console.warn('Journal functionality temporarily disabled');
    return Promise.resolve(null);
  }

  async getJournalEntry(id) {
    console.warn('Journal functionality temporarily disabled');
    return Promise.resolve(null);
  }

  async getAllJournalEntries() {
    console.warn('Journal functionality temporarily disabled');
    return Promise.resolve([]);
  }

  async updateJournalEntry(id, entryData) {
    console.warn('Journal functionality temporarily disabled');
    return Promise.resolve(null);
  }

  async deleteJournalEntry(id) {
    console.warn('Journal functionality temporarily disabled');
    return Promise.resolve(null);
  }

  // Utility methods
  async migrateFromJSON() {
    // This method will help migrate existing JSON files to the database
    console.log('Migration from JSON to IndexedDB would happen here');
  }

  async exportToJSON() {
    // Export all data to JSON format
    const posts = await this.getAllPosts();
    const archives = await this.getAllArchives();
    const journal = await this.getAllJournalEntries();
    return { posts, archives, journal };
  }
}

// Create a singleton instance
const databaseService = new DatabaseService();

export default databaseService;
